// admin-impersonate.ts — shared "view as this user" helpers for the admin UI (Task 3 of the
// admin-v2 cycle, spec docs/superpowers/specs/2026-08-06-admin-v2-internal-workspaces-design.md
// §B). Extracted out of AdminWorkspaces.tsx (Task 2, where it started as an inline
// `viewAsOwner` function) because the bot detail page's "Open owner's view" action (§B2) needs
// the exact same better-auth `admin.impersonateUser` + hard-navigate flow. Every failure throws
// a friendly `Error` so callers can surface it inline (never invisible — the billing-UX lesson).
//
// `t: Catalog` (locale-dashboard plan, Task 12) is a REQUIRED parameter on every function below,
// same policy as every other `t: Catalog` parameter in this plan (no default) — these are plain
// async helpers, not components, so they cannot resolve their own copy via `useT()`. Their two
// call sites (`AdminWorkspaces.tsx`, `AdminBotDetail.tsx`) now thread `t` through — the minimum
// change needed to keep `check-types` green — but neither page's OWN copy is touched here; that
// stays Task 13's job (Batch J, admin ops screens). `error.message` (the better-auth server
// payload) is a passthrough and stays OUT of the catalog, same as every other batch — only the
// friendly literal this app substitutes when there is no server message moved to
// `en.adminImpersonate.*`.
import type { AdminWorkspaceDetailDto } from '@repo/shared-types';
import type { Catalog } from '../locales/en';
import { authClient } from './auth-client';
import { apiFetch } from './api';

/** Starts impersonating `userId` via better-auth's admin plugin, then hard-navigates to
 * `redirectTo` (default `/`, the owner-facing app root) so every provider/query cache resets
 * fresh under the impersonated session — a client-side route change would leave stale
 * "admin's own" data in the React Query cache. */
export async function impersonateUserId(userId: string, t: Catalog, redirectTo = '/'): Promise<void> {
  const { error } = await authClient.admin.impersonateUser({ userId });
  if (error) throw new Error(error.message ?? t.adminImpersonate.impersonateFailedFallback);
  location.assign(redirectTo);
}

/** Resolves a user id from their email via the admin plugin's own `listUsers` (exact match,
 * `filterOperator: 'eq'`) — for call sites that only have an email on hand, e.g.
 * `AdminWorkspaceRow.ownerEmail` (that DTO carries no `ownerUserId`, see its doc comment). */
export async function resolveUserIdByEmail(email: string, t: Catalog): Promise<string> {
  const { data, error } = await authClient.admin.listUsers({
    query: { filterField: 'email', filterOperator: 'eq', filterValue: email, limit: 1 },
  });
  if (error) throw new Error(error.message ?? t.adminImpersonate.lookupFailedFallback);
  const user = data?.users[0];
  if (!user) throw new Error(t.adminImpersonate.userNotFound);
  return user.id;
}

/** Impersonates a workspace owner known only by email (the Workspaces table's "View as"
 * action), then redirects to `redirectTo`. */
export async function impersonateOwnerByEmail(
  email: string,
  t: Catalog,
  redirectTo = '/',
): Promise<void> {
  const userId = await resolveUserIdByEmail(email, t);
  await impersonateUserId(userId, t, redirectTo);
}

/** Impersonates the owner of `workspaceId` (the bot detail page's "Open owner's view",
 * spec §B2) by fetching the workspace detail — which carries `owner.userId` directly, no
 * email round trip needed — then redirects to `redirectTo`. */
export async function impersonateWorkspaceOwner(
  workspaceId: string,
  t: Catalog,
  redirectTo = '/',
): Promise<void> {
  const detail = await apiFetch<AdminWorkspaceDetailDto>(`/api/admin/workspaces/${workspaceId}`);
  if (!detail.owner) throw new Error(t.adminImpersonate.noOwnerToView);
  await impersonateUserId(detail.owner.userId, t, redirectTo);
}
